/**
 * Слоты ботов: (тип счёта × биржа).
 *
 * Правило приложения (2026-09-20): у юзера может быть по ОДНОМУ боту на каждом
 * типе счёта И КАЖДОЙ бирже — «демо на Bybit» и «демо на Binance» это разные
 * слоты, они не мешают друг другу. Бэкенд считает слот именно так с 2026-09-18
 * (db.countBotsByAccount(userId, accountMode, exchange) в POST /api/bots),
 * а фронт до этой правки считал слот по типу счёта ЦЕЛИКОМ:
 *
 *     userBots.some(b => botAccountType(b) === 'demo')      // было
 *
 * Поэтому демо-бот на Bybit запрещал создать демо-бота на Binance — ровно то,
 * на что жаловался владелец 2026-09-20 («подключил ключи Binance, демо-счёт, а
 * создать демо-бота не даёт — на Bybit демо уже есть»). Ещё один дефект того же
 * корня: биржа считалась «занятой» по `b.type === defaultType`, а `type` у ботов
 * с сервера нет вовсе (normalizeBot в db.js его не отдаёт, там accountMode) —
 * то есть список занятых бирж всегда был пуст.
 *
 * Здесь это правило собрано в одном месте, чтобы модалка создания бота, страница
 * «Боты» и раздел «Биржи» считали доступность одинаково.
 *
 * Канонический id биржи — нижний регистр ('bybit' | 'binance' | 'bitget'): так он
 * хранится в api_keys, так его отдаёт GET /api/exchanges и так его понимает
 * бэкенд (SUPPORTED_EXCHANGES). В bots.exchange при этом лежит display-имя
 * ('Bybit') — поэтому нормализуем оба варианта через exchangeIdOf().
 */

// Режим биржи из админки («вкл» / «в разработке» / «скрыта») — один и тот же
// код, что у раздела «Биржи» и админки (src/utils/exchangeMode.js).
import { exchangeModeOf } from './exchangeMode.js'

// Биржи, на которых реально можно создать бота (порядок — как в дропдауне).
// KuCoin и BingX из раздела «Биржи» тут отсутствуют: они в списке «Скоро».
export const BOT_EXCHANGE_IDS = ['bybit', 'binance', 'bitget']

/**
 * Биржи, на которых можно создать НОВОГО бота, по публичному конфигу админки.
 *
 * Админ может перевести биржу в «в разработке» (или скрыть) — тогда бота там
 * создать нельзя: ключи на такую биржу не подключаются, движку нечем торговать
 * (жалоба владельца 2026-10-26: Binance стояла «в разработке», а форма создания
 * всё равно её предлагала). Режим читается из appConfig.exchanges — если конфиг
 * ещё не пришёл, exchangeModeOf() считает биржу включённой (как было до режимов).
 */
export function bookableExchanges(appConfig) {
  return BOT_EXCHANGE_IDS.filter(id => exchangeModeOf(appConfig, id) === 'on')
}

// Список бирж для подсчёта слотов: явный (из режимов админки) либо все.
// Пустой массив — законное значение («ни одной биржи не открыто»), поэтому
// проверяем только «список не передан».
function exchangeList(exchanges) {
  return Array.isArray(exchanges) ? exchanges : BOT_EXCHANGE_IDS
}

// display-имя для интерфейса (карточка бота, дропдаун, тексты ошибок).
export const EXCHANGE_LABEL = {
  bybit: 'Bybit',
  binance: 'Binance',
  bitget: 'Bitget',
}

/**
 * Любое написание биржи → канонический id в нижнем регистре.
 * Понимает 'Bybit', 'bybit', 'BINANCE', пустое значение и мусор (→ bybit,
 * как и бэкенд: Bybit — дефолт и legacy-фолбэк).
 */
export function exchangeIdOf(value) {
  const s = String(value == null ? '' : value).trim().toLowerCase()
  return BOT_EXCHANGE_IDS.includes(s) ? s : 'bybit'
}

export function exchangeLabel(value) {
  return EXCHANGE_LABEL[exchangeIdOf(value)]
}

/**
 * Тип счёта бота: 'demo' | 'real'. Источник — accountMode (так пишут и БД, и
 * стор), фолбэк на params.DEMO (старые записи).
 */
export function botAccountType(bot) {
  const explicit = bot?.accountMode || bot?.account_mode
  if (explicit === 'demo' || explicit === 'real') return explicit
  return (bot?.params?.DEMO === 'True' || bot?.params?.DEMO === true) ? 'demo' : 'real'
}

/**
 * Биржа бота: bots.exchange (display-имя), фолбэк на params.Exchange (id),
 * дальше — bybit. Оба поля читаем, потому что боты приходят из разных мест:
 * с сервера (exchange), из оптимистичной вставки после создания (exchange),
 * из старых записей (params.Exchange).
 */
export function botExchange(bot) {
  return exchangeIdOf(bot?.exchange || bot?.params?.Exchange)
}

/**
 * Виртуальный «ПРОБНЫЙ» бот слота НЕ занимает — ровно как в SQL
 * (countBotsByAccount фильтрует COALESCE(virtual, 0) = 0): после пробного бота
 * юзер может создать обычного демо/реального.
 */
export function isVirtualBot(bot) {
  return !!bot?.virtual
}

// Есть ли у юзера НЕвиртуальный бот на этой паре (тип счёта × биржа).
export function slotTaken(bots, accountMode, exchange) {
  const ex = exchangeIdOf(exchange)
  const mode = accountMode === 'demo' ? 'demo' : 'real'
  return (Array.isArray(bots) ? bots : []).some(b =>
    !isVirtualBot(b) && botAccountType(b) === mode && botExchange(b) === ex
  )
}

// Биржи, ЗАНЯТЫЕ на этом типе счёта (для дропдауна выбора биржи).
export function takenExchanges(bots, accountMode) {
  return BOT_EXCHANGE_IDS.filter(ex => slotTaken(bots, accountMode, ex))
}

// Есть ли НЕвиртуальный бот на этой бирже — любого типа счёта. Нужно разделу
// «Биржи»: пока на бирже есть бот, её ключи менять нельзя (бот работает по ним).
// Считается по КАЖДОЙ бирже отдельно: бот на Bybit не мешает подключить и
// править ключи Binance (2026-09-20).
export function hasBotOnExchange(bots, exchange) {
  const ex = exchangeIdOf(exchange)
  return (Array.isArray(bots) ? bots : []).some(b => !isVirtualBot(b) && botExchange(b) === ex)
}

// Биржи, СВОБОДНЫЕ на этом типе счёта. exchanges — список бирж из режимов
// админки (bookableExchanges); не передан — все три (прежнее поведение).
export function freeExchanges(bots, accountMode, exchanges) {
  return exchangeList(exchanges).filter(ex => !slotTaken(bots, accountMode, ex))
}

// Открывает ли подписка этот тип счёта.
export function modeAllowed(accountMode, { demoAllowed = false, realAllowed = false } = {}) {
  return accountMode === 'demo' ? !!demoAllowed : !!realAllowed
}

// Можно ли вообще создать бота этого типа: подписка открывает И есть свободная
// биржа (иначе слот этого типа занят на всех биржах, открытых админом).
export function modeAvailable(bots, accountMode, allowed = {}) {
  return modeAllowed(accountMode, allowed) &&
    freeExchanges(bots, accountMode, allowed.exchanges).length > 0
}

/**
 * Начальный выбор формы создания бота — пара «тип счёта + биржа».
 *
 * Порядок предпочтений: явно переданный prefer (юзер нажал конкретную кнопку
 * «добавить бота» в контексте типа счёта) → реальный счёт → демо (как было до
 * правки 2026-09-20), а внутри типа — первая СВОБОДНАЯ биржа этого типа.
 * Exchanges (необязательно) — биржи, открытые админом: «в разработке» и
 * скрытые не предлагаются, иначе форма открывалась бы на бирже, на которой
 * создать бота нельзя (2026-10-26).
 * Если свободного слота нет вообще (все биржи заняты на обоих типах) —
 * возвращаем прежнее поведение (первый тип + первая биржа): форма откроется,
 * но кнопки типа счёта будут неактивны, а под ними будет причина.
 *
 * @returns {{accountMode:'demo'|'real', exchange:string}} exchange — канонический id
 */
export function initialSlot(bots, { demoAllowed = false, realAllowed = false, prefer = null, exchanges } = {}) {
  const allowed = { demoAllowed, realAllowed, exchanges }
  const first = (prefer === 'demo' || prefer === 'real') ? prefer : null
  for (const mode of [first, 'real', 'demo']) {
    if (!mode) continue
    if (!modeAllowed(mode, allowed)) continue
    const free = freeExchanges(bots, mode, exchanges)
    if (free.length) return { accountMode: mode, exchange: free[0] }
  }
  return { accountMode: first || 'real', exchange: exchangeList(exchanges)[0] || BOT_EXCHANGE_IDS[0] }
}

/**
 * Переключение типа счёта в форме создания бота. Если на текущей бирже такой
 * слот уже занят — уводим биржу на свободную того же типа: юзер нажал «DEMO»,
 * потому что хочет демо-бота, и упереться в занятую биржу он не должен (ровно
 * на это жаловался владелец 2026-09-20: демо-бот на Bybit не давал создать
 * демо-бота на Binance, хотя демо-слот Binance был пуст).
 *
 * @returns {{accountMode, exchange}|null} null — тип счёта недоступен
 *          (нет подписки либо все биржи этого типа заняты).
 */
export function switchAccountMode(bots, mode, currentExchange, allowed = {}) {
  const next = mode === 'demo' ? 'demo' : 'real'
  if (!modeAvailable(bots, next, allowed)) return null
  const free = freeExchanges(bots, next, allowed.exchanges)
  const cur = exchangeIdOf(currentExchange)
  return { accountMode: next, exchange: free.includes(cur) ? cur : free[0] }
}
